<template>
  <ChartBox
    class="top-publication"
    :title="tableInfo.title"
    :description="tableInfo.description"
    fullwidth
  >
    <SkeletonTable
      v-if="isLoading"
      :count-table-column="countTableColumn"
    />

    <ErrorDefault
      :show="hasError"
      data-testid="error-top-publication"
      @onClickButton="getTopPublication()"
    />

    <template #primary-action>
      <DownloadMenu
        v-if="!isLoading && !hasError"
        track-name-on-open="BM ExportMidiaPostTop Table Open"
        :download-list="[
          { label: 'CSV', type: 'csv' },
          { label: 'XLS', type: 'xls' },
        ]"
        @selectDownload="handleDownload"
      />
    </template>

    <div
      v-if="!isLoading && !hasError"
      v-observe.once="observer"
    >
      <v-data-table
        :headers="customHeaders"
        :items="publicationList"
        :items-per-page="itemsPerPage"
        :page.sync="page"
        :hide-default-footer="true"
        :sort-by="'engagement_rate'"
        :sort-desc="true"
        class="top-publication__table"
        @page-count="pageCount = $event"
        @pagination="paginationInfo = $event"
        @update:sort-by="trackSortBy"
      >
        <template
          v-for="(h, headerKey) in headers"
          v-slot:[`header.${h.value}`]="{ header }"
          text-right
        >
          <TableHeaderItem
            :key="headerKey"
            :text="header.text"
            :tooltip-text="header.tooltip"
            :class="{
              'table-header-item--modify': modifyHeaderColumn(h),
              'table-header-item--engagement': addShortClass && h.value === 'engagement_rate',
              'table-header-item--interaction': addShortClass && h.value === 'interactions_total'
            }"
            @show-tooltip="trackTooltipUsage"
          />
        </template>

        <template v-slot:[`item.publication`]="{ item }">
          <CardPublication
            :publication="item.publication"
            data-testid="card-publication"
            :class="{ 'card-publication--short': addShortClass }"
            @click-on-see-post="trackSeePost(item.publication)"
          />
        </template>

        <template v-slot:[`item.engagement_rate`]="{ item }">
          {{ item.engagement_rate_formatted }}
        </template>

        <template v-slot:[`item.likes_count`]="{ item }">
          {{ item.likes_count_formatted }}
        </template>

        <template v-slot:[`item.comments_count`]="{ item }">
          {{ item.comments_count_formatted }}
        </template>

        <template v-slot:[`item.shares_count`]="{ item }">
          {{ item.shares_count_formatted }}
        </template>

        <template v-slot:[`item.interactions_total`]="{ item }">
          {{ item.interactions_total_formatted }}
        </template>

        <template v-slot:[`item.polarity`]="{ item }">
          <FeelingList
            :polarity="item.polarity"
          />
        </template>
      </v-data-table>
      <div class="custom-table-footer">
        <div>
          <p class="custom-table-footer__show-quantity">
            {{ showQuantityPage }}
          </p>
        </div>

        <div class="custom-table-footer__pagination">
          <button
            class="custom-table-footer__page-first"
            :disabled="disableButtonToFirstPage"
            data-testid="button-first-page"
            @click="toFirstPage"
          >
            <DsIcon
              name="page-first"
              class="custom-table-footer__arrow-icon"
            />
          </button>

          <v-pagination
            v-model="page"
            :length="pageCount"
            :circle="true"
            previous-aria-label="Ir para o anterior"
            next-aria-label="Ir para o próximo"
            @next="dispatchTrackEvent('nextPageTable')"
            @previous="dispatchTrackEvent('previousPageTable')"
          />

          <button
            class="custom-table-footer__page-last"
            :disabled="disableButtonToLastPage"
            data-testid="button-last-page"
            @click="toLastPage"
          >
            <DsIcon
              name="page-last"
              class="custom-table-footer__arrow-icon"
            />
          </button>
        </div>
      </div>
    </div>
  </ChartBox>
</template>

<script>
import { DsIcon } from '@ds/icons';
import { ExportToCsv } from 'export-to-csv';
import { json2excel } from 'js2excel';

import {
  dispatchMixpanelTrack,
  formatTimestampToDate,
  addEllipsisPointsAboveCharactersLimit,
  generateFileName,
  numberFormatToPtBr,
} from '@/utils';

import { ErrorDefault, DownloadMenu, ChartBox } from 'components/base';
import { TableHeaderItem } from '@/components/content/TableHeaderItem';
import { CardPublication } from '@/components/content/CardPublication';
import { FeelingList } from '@/components/content/FeelingList';
import { SkeletonTable } from '@/components/content/tables/SkeletonTable';

export default {
  name: 'TopPublicationByChannel',
  components: {
    ChartBox,
    DsIcon,
    TableHeaderItem,
    CardPublication,
    FeelingList,
    ErrorDefault,
    DownloadMenu,
    SkeletonTable,
  },
  data () {
    return {
      title: 'Top publicações',
      page: 1,
      pageCount: 0,
      itemsPerPage: 8,
      paginationInfo: {},
    };
  },
  computed: {
    headers() {
      return [
        {
          text: 'Publicação',
          value: 'publication',
          sort: (a, b) => a.posted_at_timestamp - b.posted_at_timestamp,
        },
        {
          text: 'Índice de engajamento',
          align: 'right',
          tooltip: `
            O índice de engajamento = (curtidas + comentários x 1,5 + compartilhamentos x 2) / número de fãs) x 1000 no período selecionado.
          `,
          value: 'engagement_rate',
        },
        {
          text: 'Curtidas / Gostei',
          align: 'right',
          value: 'likes_count',
        },
        {
          text: 'Comentários',
          align: 'right',
          value: 'comments_count',
        },
        {
          text: 'Compartilhamento',
          align: 'right',
          value: 'shares_count',
        },
        {
          text: 'Total de interações',
          align: 'right',
          tooltip: `
            Total de interações é a soma de reações, comentários e compartilhamento.
          `,
          value: 'interactions_total',
        },
        {
          text: 'Sentimento nos comentários',
          align: 'right',
          sortable: false,
          value: 'polarity',
        },
      ];
    },
    customHeaders() {
      const headerList = this.headers;
      const socialName = this.$store.state.ui.channel.toUpperCase();

      const removePolarityColumn = (header) => header.value !== 'polarity';
      const removeShareColumn = (header) => header.value !== 'shares_count';

      const socialNetworkConfig = {
        INSTAGRAM: {
          engagement_rate: 'O índice de engajamento = (curtidas + comentários x 1,5) / número de fãs) x 1000 no período selecionado.',
          interactions_total: 'Total de interações é a soma de curtidas/gostei e comentários.',
          customHeaderList: headerList.filter((header) => removePolarityColumn(header) && removeShareColumn(header)),

        },
        TWITTER: {
          engagement_rate: 'O índice de engajamento = (curtidas + comentários x 1,5 + compartilhamentos x 2) / número de fãs) x 1000 no período selecionado.',
          interactions_total: 'Total de interações é a soma de curtidas e compartilhamentos.',
          customHeaderList: headerList,
        },
        YOUTUBE: {
          engagement_rate: 'O índice de engajamento = (curtidas + comentários x 1,5) / número de views) x 1000 no período selecionado',
          interactions_total: 'Total de interações é a soma de curtidas e comentários.',
          customHeaderList: headerList.filter((header) => removeShareColumn(header)),
        },
      };

      const changeTooltipText = (header, message) => ({
        ...header,
        tooltip: message,
      });

      const changeTooltipTextFromHeaderList = (list, socialNetwork) => {
        return list.map((header) => {
          const engagementRate = 'engagement_rate';
          const interactionsTotal = 'interactions_total';

          if (header.value === engagementRate) return changeTooltipText(header, socialNetwork[engagementRate]);

          if (header.value === interactionsTotal) return changeTooltipText(header, socialNetwork[interactionsTotal]);

          return header;
        });
      };

      if (socialName in socialNetworkConfig) {
        const headerConfigBySocialName = socialNetworkConfig[socialName];

        return changeTooltipTextFromHeaderList(headerConfigBySocialName.customHeaderList, headerConfigBySocialName);
      }

      return headerList;
    },
    getPageStart() {
      if (this.publicationList.length === 0) return 0;

      if (this.publicationList.length > 0 && this.paginationInfo.pageStart === 0) return 1;

      return this.paginationInfo.pageStart ? this.paginationInfo.pageStart + 1 : 0;
    },
    getPageStop() {
      return this.paginationInfo.pageStop ? this.paginationInfo.pageStop : 0;
    },
    getPublicationQuantity() {
      return this.publicationList.length;
    },
    showQuantityPage() {
      return `Mostrando ${this.getPageStart}-${this.getPageStop} de ${this.getPublicationQuantity} publicações`;
    },
    disableButtonToFirstPage() {
      return this.page === 1;
    },
    disableButtonToLastPage() {
      return this.page === this.pageCount;
    },
    isLoading() {
      return this.$store.state.charts.topPublicationByChannel.isLoading;
    },
    hasError() {
      return this.$store.state.charts.topPublicationByChannel.hasError;
    },
    publicationList() {
      return this.formatPublicationList(this.$store.state.charts.topPublicationByChannel.data);
    },
    dataToExport() {
      return this.publicationList.map((p) => {
        const publicationJoined = [
          `Página: ${p.publication.name}`,
          `Data: ${p.publication.posted_at}`,
          `Descrição: ${p.publication.description}`,
          `Url: ${p.publication.url}`,
        ].join('\n');

        const polarityJoined = [
          `Positivo: ${p.polarity.positive}`,
          `Neutro: ${p.polarity.neutral}`,
          `Negativo: ${p.polarity.negative}`,
        ].join('\n');

        const keyList = Object.entries(p).map(([key]) => key);

        return keyList.reduce((acc, key) => {
          const keyName = this.headers.find(h => h.value === key)?.text;

          if (!keyName) return acc;

          acc[keyName] = p[key];

          if (key === 'publication') {
            acc[keyName] = publicationJoined;
          }

          if (key === 'polarity') {
            acc[keyName] = polarityJoined;
          }

          return acc;
        }, {});
      });
    },
    filename() {
      return generateFileName(this.title);
    },
    observer() {
      return {
        onObserver: this.onObserver,
        options: {
          threshold: 0.5,
        },
      };
    },
    tableInfo() {
      return {
        title: !this.isLoading ? this.title : '',
        description: !this.isLoading ? 'Top 3 publicações por grupo com o maior índice de engajamento no período selecionado.' : '',
      };
    },
    countTableColumn() {
      const socialName = this.$store.state.ui.channel.toUpperCase();

      const columnBySocialName = {
        YOUTUBE: 6,
        INSTAGRAM: 5,
        DEFAULT: 7,
      };
      return socialName in columnBySocialName ? columnBySocialName[socialName] : columnBySocialName.DEFAULT;
    },
    addShortClass() {
      const socialName = this.$store.state.ui.channel.toUpperCase();
      const socialList = ['FACEBOOK', 'TWITTER'];

      return socialList.includes(socialName);
    },
  },
  async mounted() {
    const isNoneRequestRunning = !this.isLoading;

    if (isNoneRequestRunning) {
      await this.getTopPublication();
    }
  },
  methods: {
    async getTopPublication() {
      await this.$store.dispatch('charts/fetchTopPublications');
    },
    toFirstPage() {
      this.dispatchTrackEvent('firstPageTable');
      this.page = 1;
    },
    toLastPage() {
      this.dispatchTrackEvent('lastPageTable');
      this.page = this.pageCount;
    },
    formatPublicationList(data) {
      const formatPolarityData = (polarity) => `${Math.floor(polarity)}%`;

      const formatNumber = (value) => Intl.NumberFormat('pt-Br').format(value);

      return data.map(({ page, post, metrics, polarity }) => ({
        publication: {
          img: page.image,
          name: `@${page.name}`,
          posted_at_timestamp: post.postedAt,
          posted_at: formatTimestampToDate(post.postedAt),
          description: addEllipsisPointsAboveCharactersLimit(post.text, 67),
          url: post.postUrl,
        },
        engagement_rate: metrics.engagementRate,
        engagement_rate_formatted: numberFormatToPtBr(metrics.engagementRate),
        likes_count: metrics.likesCount,
        likes_count_formatted: formatNumber(metrics.likesCount),
        comments_count: metrics.commentsCount,
        comments_count_formatted: formatNumber(metrics.commentsCount),
        interactions_total: metrics.interactionsTotal,
        interactions_total_formatted: formatNumber(metrics.interactionsTotal),
        shares_count: metrics.sharesCount,
        shares_count_formatted: formatNumber(metrics.sharesCount),
        polarity: {
          negative: formatPolarityData(polarity.negative),
          positive: formatPolarityData(polarity.positive),
          neutral: formatPolarityData(polarity.neutral),
        },
      }));
    },
    dispatchTrackEvent(name) {
      const trackNames = {
        nextPageTable: 'BM MidiaPostTop PageNextTable Click',
        previousPageTable: 'BM MidiaPostTop PageBackTable Click',
        firstPageTable: 'BM MidiaPostTop PageEndTable Click',
        lastPageTable: 'BM MidiaPostTop PageHomeTable Click',
      };

      if (!(name in trackNames)) throw new Error('name parameter must be valid property name');

      dispatchMixpanelTrack(trackNames[name], ['network']);
    },
    trackTooltipUsage(text) {
      dispatchMixpanelTrack('BM MidiaPostTop TooltipTable View', ['network', 'tab'], {
        answer: text,
      });
    },
    trackSeePost(publication) {
      dispatchMixpanelTrack('BM MidiaPostTop LegendTable Click', ['network'], {
        answer: publication.url,
      });
    },
    trackSortBy(sortBy) {
      if (sortBy) {
        const trackName = 'BM MidiaPostTop ReorderTable Click';

        dispatchMixpanelTrack(trackName, ['network', 'tab'], {
          answer: this.headers.find(header => sortBy.includes(header.value))?.text,
        });
      }
    },
    onObserver() {
      dispatchMixpanelTrack('BM MidiaPostTop Graph View', ['network']);
    },
    dispatchTrackOnDownload(type) {
      dispatchMixpanelTrack('BM ExportMidiaPostTop Table Click', ['network', 'tab', 'groups'], {
        answer: type,
      });
    },
    handleDownloadCSV() {
      const options = {
        fieldSeparator: ';',
        showLabels: true,
        useKeysAsHeaders: true,
        useBom: true,
        filename: this.filename,
      };

      new ExportToCsv(options).generateCsv(this.dataToExport);
    },
    handleDownloadXLS() {
      json2excel({
        data: this.dataToExport,
        name: this.filename,
      });
    },
    handleDownload({ typeSelected }) {
      const exportTypes = {
        csv: 'handleDownloadCSV',
        xls: 'handleDownloadXLS',
      };

      if (!(typeSelected in exportTypes)) return;

      const functionName = exportTypes[typeSelected];

      this[functionName]();
      this.dispatchTrackOnDownload(typeSelected);
    },
    modifyHeaderColumn(header) {
      return header.value === 'engagement_rate' || header.value === 'interactions_total';
    },
  },
};
</script>

<style lang="scss" scoped>
.top-publication {
  position: relative;

  &__table {
    border-radius: var(--ds-border-radius-sm) var(--ds-border-radius-sm) 0 0;
  }

  ::v-deep {
    .polarity-list {
      justify-content: flex-end;

      i {
        width: var(--ds-spacing-stack-xs);
        height: var(--ds-spacing-stack-xs);
      }

      span {
        font-weight: 600;
      }
    }

    .v-data-table__wrapper table {
      thead tr {
        th {
          position: relative;
          padding-top: 9.5px;
          padding-bottom: 9.5px;

          span {
            font-weight: 600;
          }
        }

        &:last-child > th {
          border-bottom: thin solid var(--ds-color-silver);
        }
      }

      tbody {
        td {
          font-size: var(--ds-font-size-sm);
          font-weight: 600;
        }

        tr:not(:last-child) > td:not(.v-data-table__mobile-row) {
          border-bottom: thin solid var(--ds-color-silver);
        }
      }
    }

    .table-header-item--modify {
      display: inline-flex;
      align-items: center;
      margin-right: var(--ds-spacing-stack-xs);

      span.table-header-item__text {
        display: inline-block;
        max-width: 133px;
        text-align: left;
      }

      span.table-header-item__icon-questions {
        margin-right: var(--ds-spacing-stack-nano);
      }

      & + i {
        position: absolute;
        right: var(--ds-spacing-stack-xs);
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .table-header-item--engagement span.table-header-item__text {
      max-width: 78px;
    }

    .table-header-item--interaction span.table-header-item__text {
      max-width: 62px;
    }

    .card-publication--short {
      grid-template-columns: max-content 180px;
    }

    .desc .table-header-item--modify {
      & + i {
        transform: translateY(-50%) rotate(180deg);
      }
    }

    .download-menu {
      top: 0;
      right: 0;
    }
  }
}

.custom-table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-spacing-stack-xs) var(--ds-spacing-inline-xs);
  border: 1px solid var(--ds-color-silver);
  border-top: 0;
  border-radius: 0 0 var(--ds-border-radius-sm) var(--ds-border-radius-sm);

  &__show-quantity {
    margin-bottom: 0;
    font-size: var(--ds-font-size-xs);
    font-weight: 600;
    color: var(--ds-color-text-1);
  }

  &__pagination {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    min-width: 500px;
  }

  &__page-first {
    margin-right: var(--ds-spacing-stack-nano);
  }

  &__page-first,
  &__page-last {
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--ds-spacing-stack-sm);
    height: var(--ds-spacing-stack-sm);
    border-radius: 50%;

    .ds-icon {
      width: var(--ds-spacing-stack-xs);
      height: var(--ds-spacing-stack-xs);
    }

    &:disabled {
      background-color: var(--ds-color-background);
    }
  }

  ::v-deep {
    nav ul li {
      margin-right: var(--ds-spacing-stack-nano);
    }

    .v-pagination__item {
      margin: 0;
      min-width: var(--ds-spacing-stack-sm);
      height: var(--ds-spacing-stack-sm);
      font-size: var(--ds-font-size-xs);
      font-weight: 600;
      box-shadow: none;

      &:not(.v-pagination__item--active) {
        color: var(--ds-color-text-2);
      }
    }

    .v-pagination__navigation {
      width: var(--ds-spacing-stack-sm);
      height: var(--ds-spacing-stack-sm);
      margin: 0;
      box-shadow: none;

      &--disabled {
        opacity: 1;
        background-color: var(--ds-color-background);
      }

      .v-icon {
        font-size: var(--ds-spacing-stack-xs);
      }
    }
  }
}
</style>
